import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import type { ContactResponse, Contact, FieldMapping } from '../types/contact';
import type { FieldDef } from '../types/cases';

/* ─────────────────────────────────────────────
 * 1. Fetch single contact + associated cases
 * ───────────────────────────────────────────── */

export function useContact(contactId: string | undefined) {
  return useQuery<ContactResponse>({
    queryKey: ['contact', contactId],
    queryFn: async () => {
      const { data } = await api.get(`/api/contacts/${contactId}`);
      return data as ContactResponse;
    },
    enabled: !!contactId,
  });
}

/* ─────────────────────────────────────────────
 * 2. Update contact (PUT /api/contacts/:id)
 * ───────────────────────────────────────────── */

export function useUpdateContact(contactId: string | undefined) {
  const qc = useQueryClient();

  return useMutation<unknown, Error, Partial<Contact>>({
    mutationFn: async (updates) => {
      const { data } = await api.put(`/api/contacts/${contactId}`, updates);
      return data;
    },
    onSuccess: () => {
      // Re-fetch contact data after save
      qc.invalidateQueries({ queryKey: ['contact', contactId] });
    },
  });
}

/* ─────────────────────────────────────────────
 * 3. Dynamic contact field definitions
 *    Reuses GET /api/fieldconfig/fields, filtered
 *    to source_table = 'contacts'.
 * ───────────────────────────────────────────── */

/** Map field_type → the icon we used historically (fallback) */
const ICON_FALLBACK: Record<string, string> = {
  text: 'info-circle',
  date: 'calendar',
  datetime: 'calendar',
};

export function useContactFieldDefs() {
  return useQuery<Record<string, FieldMapping>>({
    queryKey: ['contact-field-defs'],
    queryFn: async () => {
      const { data } = await api.get('/api/fieldconfig/fields');
      if (!data.success || !data.data) return {};

      const allFields = data.data as FieldDef[];
      const contactFields = allFields.filter((f) => f.source_table === 'contacts');

      // Build field_key → FieldMapping lookup
      // (same shape as the old hardcoded FIELD_MAP)
      const map: Record<string, FieldMapping> = {};
      for (const f of contactFields) {
        const isReadonly = f.validation_rules?.readonly === true;
        const ft = f.field_type?.toLowerCase() ?? 'text';
        map[f.key] = {
          label: f.name,
          icon: ICON_FALLBACK[ft] ?? 'info-circle',
          editable: !isReadonly,
          type: ft === 'text' ? undefined : (ft as 'date' | 'datetime'),
        };
      }
      return map;
    },
    staleTime: 5 * 60_000, // cache 5 min — field defs rarely change
  });
}

/* ─────────────────────────────────────────────
 * 4. Delete contact (superadmin only)
 * ───────────────────────────────────────────── */

export function useDeleteContact(contactId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.delete(`/api/contacts/${contactId}`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact', contactId] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

/* ─────────────────────────────────────────────
 * 5. Portal invite status + send invite
 * ───────────────────────────────────────────── */

export interface PortalInviteStatus {
  has_portal_account: boolean;
  portal_user: { id: number; created_at: string; last_login_at: string | null } | null;
  invitations: Array<{
    id: number;
    email: string;
    status: string;
    expires_at: string;
    accepted_at: string | null;
    created_at: string;
  }>;
}

export function usePortalInviteStatus(contactId: string | undefined) {
  return useQuery<PortalInviteStatus>({
    queryKey: ['portalInvite', contactId],
    queryFn: async () => {
      const { data } = await api.get(`/api/portal-manage/invite/${contactId}`);
      return data;
    },
    enabled: !!contactId,
  });
}

export function useSendPortalInvite(contactId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/api/portal-manage/invite/${contactId}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portalInvite', contactId] }),
  });
}

export function useResendPortalInvite(contactId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/api/portal-manage/invite/${contactId}/resend`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portalInvite', contactId] }),
  });
}
